/**
 * Pure helpers for the `filteredModules` and `mobileRailGrouped` memos in
 * layout/Sidebar.tsx.
 *
 * Behavior-preserving extraction: only the memo BODIES moved here. The
 * orchestrator retains both `useMemo` shells with their byte-identical
 * dependency arrays and calls these functions inside them, so hook order and
 * memoization semantics are unchanged.
 */

import type React from 'react';
import { MOBILE_RAIL_GROUPS } from '../constants';
import type { SidebarModuleItem } from '../types';

interface ComputeFilteredModulesArgs {
  tempModuleOrder: SidebarModuleItem[] | null;
  allModulesSorted: SidebarModuleItem[];
  normalizedSearchQuery: string;
}

export function computeFilteredModules({
  tempModuleOrder,
  allModulesSorted,
  normalizedSearchQuery,
}: ComputeFilteredModulesArgs): SidebarModuleItem[] {
  const source = tempModuleOrder ?? allModulesSorted;
  if (normalizedSearchQuery) {
    return source.filter((item) => (item.displayName || '').toLowerCase().includes(normalizedSearchQuery));
  }
  return source;
}

export interface MobileRailGroup {
  key: string;
  label: string;
  types: string[];
  icon: React.ReactNode;
  modules: SidebarModuleItem[];
}

/** Group modules by rail type for mobile rail-style display. */
export function computeMobileRailGrouped(filteredModules: SidebarModuleItem[]): MobileRailGroup[] {
  const assigned = new Set<string>();
  return MOBILE_RAIL_GROUPS
    .map((group) => {
      const modules = filteredModules.filter((item) => {
        if (assigned.has(item.module.id)) return false;
        if (group.types.includes(item.module.type)) {
          assigned.add(item.module.id);
          return true;
        }
        return false;
      });
      // Pages: reverse to show newest first (API returns oldest-first by position)
      if (group.key === 'pages' && modules.length > 1) {
        modules.reverse();
      }
      return { ...group, modules };
    })
    .filter((g) => g.modules.length > 0);
}
